import apiClient from './apiClient';
import type {
  UserAccessDto,
  ScreenPermissionDto,
  UserPermissionsDto,
  ScreenDefinitionDto,
  CreateUserAccessRequest,
  UpdateUserAccessRequest,
  UpdatePermissionsRequest
} from '../types';

/**
 * Service para controle de acesso de usuarios
 */
export const accessControlService = {
  // ============================================================================
  // My Permissions (Usuario Logado)
  // ============================================================================

  /**
   * Obtem permissoes do usuario logado
   */
  async getMyPermissions(): Promise<UserPermissionsDto> {
    const response = await apiClient.get<UserPermissionsDto>('/access-control/my-permissions');
    return response.data;
  },

  // ============================================================================
  // Bootstrap (Primeiro Admin)
  // ============================================================================

  /**
   * Cria o primeiro usuario administrador (bootstrap)
   * So funciona se nao existir nenhum usuario cadastrado
   */
  async bootstrap(): Promise<UserAccessDto> {
    const response = await apiClient.post<UserAccessDto>('/access-control/bootstrap');
    return response.data;
  },

  // ============================================================================
  // Users Management
  // ============================================================================

  /**
   * Lista todos os usuarios (apenas admin)
   */
  async getUsers(): Promise<UserAccessDto[]> {
    const response = await apiClient.get<UserAccessDto[]>('/access-control/users');
    return response.data;
  },

  /**
   * Obtem usuario por email (apenas admin)
   */
  async getUser(email: string): Promise<UserAccessDto> {
    const response = await apiClient.get<UserAccessDto>(
      `/access-control/users/${encodeURIComponent(email)}`
    );
    return response.data;
  },

  /**
   * Cria novo usuario (apenas admin)
   */
  async createUser(request: CreateUserAccessRequest): Promise<UserAccessDto> {
    const response = await apiClient.post<UserAccessDto>('/access-control/users', request);
    return response.data;
  },

  /**
   * Atualiza usuario (apenas admin)
   */
  async updateUser(email: string, request: UpdateUserAccessRequest): Promise<UserAccessDto> {
    const response = await apiClient.put<UserAccessDto>(
      `/access-control/users/${encodeURIComponent(email)}`,
      request
    );
    return response.data;
  },

  /**
   * Remove usuario (apenas admin)
   */
  async deleteUser(email: string): Promise<void> {
    await apiClient.delete(`/access-control/users/${encodeURIComponent(email)}`);
  },

  /**
   * Alterna status ativo/inativo do usuario (apenas admin)
   */
  async toggleUserActive(email: string): Promise<UserAccessDto> {
    const response = await apiClient.patch<UserAccessDto>(
      `/access-control/users/${encodeURIComponent(email)}/toggle-active`
    );
    return response.data;
  },

  /**
   * Alterna status admin do usuario (apenas admin)
   */
  async toggleUserAdmin(email: string): Promise<UserAccessDto> {
    const response = await apiClient.patch<UserAccessDto>(
      `/access-control/users/${encodeURIComponent(email)}/toggle-admin`
    );
    return response.data;
  },

  // ============================================================================
  // Permissions Management
  // ============================================================================

  /**
   * Obtem permissoes de um usuario (apenas admin)
   */
  async getUserPermissions(email: string): Promise<ScreenPermissionDto[]> {
    const response = await apiClient.get<ScreenPermissionDto[]>(
      `/access-control/users/${encodeURIComponent(email)}/permissions`
    );
    return response.data;
  },

  /**
   * Atualiza permissoes de um usuario (apenas admin)
   */
  async updateUserPermissions(email: string, request: UpdatePermissionsRequest): Promise<ScreenPermissionDto[]> {
    const response = await apiClient.put<ScreenPermissionDto[]>(
      `/access-control/users/${encodeURIComponent(email)}/permissions`,
      request
    );
    return response.data;
  },

  // ============================================================================
  // Screens
  // ============================================================================

  /**
   * Lista todas as telas disponiveis no sistema
   */
  async getScreens(): Promise<ScreenDefinitionDto[]> {
    const response = await apiClient.get<ScreenDefinitionDto[]>('/access-control/screens');
    return response.data;
  }
};
